import React, { createContext, useContext, ReactNode } from 'react';

// Define the shape of your Django data - customize this based on your actual data structure
interface DjangoData {
  user?: {
    id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    is_authenticated: boolean;
  };
  settings?: {
    [key: string]: any;
  };
  urls?: {
    [key: string]: string;
  };
  csrfToken?: string;
  [key: string]: any; // Allow for additional properties
}

interface DjangoContextType {
  data: DjangoData;
  user: DjangoData['user'] | null;
  isAuthenticated: boolean;
  csrfToken: string | null;
}

// Create the context
const DjangoContext = createContext<DjangoContextType | undefined>(undefined);

// Provider props
interface DjangoProviderProps {
  children: ReactNode;
  djangoData?: DjangoData;
}

// Provider component
export const DjangoDataProvider: React.FC<DjangoProviderProps> = ({ 
  children, 
  djangoData = {} 
}) => {
  const contextValue: DjangoContextType = {
    data: djangoData,
    user: djangoData.user || null,
    isAuthenticated: djangoData.user?.is_authenticated || false,
    csrfToken: djangoData.csrfToken || null,
  };

  return (
    <DjangoContext.Provider value={contextValue}>
      {children}
    </DjangoContext.Provider>
  );
};

// Custom hook to use Django context
export const useDjangoData = (): DjangoContextType => {
  const context = useContext(DjangoContext);
  if (context === undefined) {
    throw new Error('useDjango must be used within a DjangoProvider');
  }
  return context;
};

// Individual hooks for common use cases
export const useDjangoUser = () => {
  const { user, isAuthenticated } = useDjangoData();
  return { user, isAuthenticated };
};

export const useDjangoAuth = () => {
  const { isAuthenticated, csrfToken } = useDjangoData();
  return { isAuthenticated, csrfToken };
};

export default DjangoContext; 